import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Compass, MapPin, Clock, Calendar, User, Tag, Globe,
  Play, ExternalLink, Edit3, Trash2, Upload, Image as ImageIcon, BookOpen,
} from 'lucide-react';
import Modal from '../../components/common/Modal';
import Loading from '../../components/common/Loading';
import TourViewer from '../../components/common/TourViewer';
import { virtualToursAPI } from '../../services/api';
import { useAppContext } from '../../context/AppContext';
import { useRoleData } from '../../hooks/useRoleData';
import { hasPermission, PERMISSIONS } from '../../utils/rbac';
import { BASE_URL, getStaticUrl } from '../../constants/config';
import { formatDate } from '../../utils/dateFormatter';
import '../ContentDetails/ContentDetails.css';
import './VirtualTours.css';
import './VirtualTourDetails.css';

const PLACEHOLDER_IMAGE = `${BASE_URL}/uploads/assets/placehold_content.svg`;
const DEFAULT_CATEGORIES = ['Onboarding', 'Technical', 'Safety', 'Certification', 'Marine'];

const EMPTY_FORM = {
  title: '', subtitle: '', description: '', tour_url: '', thumbnail_url: '',
  category: 'Onboarding', location: '', duration: '', tags: '', status: 'Active',
};

const VirtualTourDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification, currentUser } = useAppContext();
  const { filteredModules } = useRoleData();
  const canManage = hasPermission(currentUser, PERMISSIONS.MANAGE_VIRTUAL_TOURS);
  // Only Admins/Instructors see which modules a tour is used in.
  const canSeeModules = currentUser?.role === 'Administrator' || currentUser?.role === 'Instructor';
  const hasNavigation = !!location.state;

  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  // Popup iframe viewer
  const [viewing, setViewing] = useState(false);

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const thumbInputRef = useRef(null);

  // Delete confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadTour = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await virtualToursAPI.getTourById(id);
      const data = res.data?.data;
      if (!data) {
        setNotFound(true);
      } else {
        setTour(data);
        // Self-correct numeric URLs to the canonical dashless GUID form.
        if (/^\d+$/.test(id) && data.tourId) {
          navigate(`/virtual-tours/${data.tourId.replace(/-/g, '')}`, {
            replace: true,
            state: location.state,
          });
        }
      }
    } catch (err) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, location.state]);

  useEffect(() => { loadTour(); }, [loadTour]);

  useEffect(() => {
    virtualToursAPI.getCategories()
      .then(res => setCategories(res.data?.data?.length ? res.data.data : DEFAULT_CATEGORIES))
      .catch(() => {});
  }, []);

  const openEdit = () => {
    setForm({
      title: tour.title || '',
      subtitle: tour.subtitle || '',
      description: tour.description || '',
      tour_url: tour.tourUrl || '',
      thumbnail_url: tour.thumbnail || '',
      category: tour.category || 'Onboarding',
      location: tour.location || '',
      duration: tour.duration != null ? String(tour.duration) : '',
      tags: (tour.tags || []).join(', '),
      status: tour.status || 'Active',
    });
    setEditOpen(true);
  };

  const handleThumbFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      showNotification('Please select a JPEG, PNG, GIF, or WEBP image', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showNotification('Image must be under 10MB', 'error');
      return;
    }
    setUploadingThumb(true);
    try {
      const fd = new FormData();
      fd.append('thumbnail', file);
      const res = await virtualToursAPI.uploadImage(fd);
      const url = res.data?.data?.url;
      if (url) {
        setForm(f => ({ ...f, thumbnail_url: url }));
        showNotification('Image uploaded', 'success');
      }
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to upload image', 'error');
    } finally {
      setUploadingThumb(false);
      if (thumbInputRef.current) thumbInputRef.current.value = '';
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!form.title.trim() || !form.tour_url.trim()) {
      showNotification('Title and tour URL are required', 'warning');
      return;
    }
    const payload = {
      title: form.title.trim(),
      subtitle: form.subtitle.trim() || null,
      description: form.description.trim(),
      tour_url: form.tour_url.trim(),
      thumbnail_url: form.thumbnail_url.trim() || null,
      category: form.category,
      location: form.location.trim() || null,
      duration: form.duration ? parseInt(form.duration, 10) : null,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      status: form.status,
    };
    setSaving(true);
    try {
      await virtualToursAPI.updateTour(tour.tourId.replace(/-/g, ''), payload);
      showNotification('Virtual tour updated', 'success');
      setEditOpen(false);
      loadTour();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      showNotification(msg || 'Failed to save virtual tour', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await virtualToursAPI.deleteTour(tour.tourId.replace(/-/g, ''));
      showNotification('Virtual tour deleted', 'success');
      navigate('/virtual-tours');
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to delete', 'error');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="empty-state">
        <Loading fullScreen={false} message="Loading virtual tour..." />
      </div>
    );
  }

  if (notFound || !tour) {
    return (
      <div className="empty-state">
        <h3>Virtual tour not found or you don't have access</h3>
        <button className="btn btn-outline" onClick={() => navigate('/virtual-tours')}>
          Back to Virtual Tours
        </button>
      </div>
    );
  }

  const thumbnail = getStaticUrl(tour.thumbnail) || PLACEHOLDER_IMAGE;
  const thumbPreview = form.thumbnail_url ? getStaticUrl(form.thumbnail_url) : '';

  // Modules that include this tour (reverse lookup via module.tourIds).
  const associatedModules = canSeeModules
    ? filteredModules.filter((m) => (m.tourIds || []).includes(tour.id))
    : [];

  return (
    <div className="content-details vt-detail">
      {/* Back navigation */}
      <div className="content-details__back-nav">
        {hasNavigation && (
          <>
            <button className="btn btn-ghost content-details__back" onClick={() => navigate(-1)} title="Go back">
              <ArrowLeft size={16} /> Back
            </button>
            <span className="content-details__back-separator">|</span>
          </>
        )}
        <button className="btn btn-link content-details__back-parent" onClick={() => navigate('/virtual-tours')} title="Back to Virtual Tours">
          Virtual Tours
        </button>
      </div>

      {/* Hero */}
      <div className="content-details__hero card">
        <div className="content-details__hero-thumb vt-detail__hero-thumb">
          <img
            src={thumbnail}
            alt={tour.title}
            onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
          />
          <div className="content-details__hero-overlay" />
          <button className="vt-detail__hero-play" onClick={() => setViewing(true)} title="Launch tour">
            <Play size={22} />
          </button>
        </div>
        <div className="content-details__hero-info">
          <div className="content-details__hero-badges">
            <span className="badge badge-green">{tour.category}</span>
            <span className={`badge ${tour.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
              {tour.status}
            </span>
            <span className="badge badge-dark">🌐 Web Tour</span>
          </div>
          <h1 className="content-details__hero-title">{tour.title}</h1>
          {tour.subtitle && <p className="content-details__hero-subtitle">{tour.subtitle}</p>}
          {tour.description && <p className="content-details__hero-desc">{tour.description}</p>}
          <div className="content-details__hero-meta">
            {tour.location && <span><MapPin size={14} /> {tour.location}</span>}
            {tour.duration != null && <span><Clock size={14} /> {tour.duration} min</span>}
            <span><Calendar size={14} /> Added {formatDate(tour.createdAt)}</span>
            {tour.createdByName && <span><User size={14} /> By {tour.createdByName}</span>}
          </div>
          <div className="content-details__hero-actions">
            <button className="btn btn-primary" onClick={() => setViewing(true)}>
              <Play size={15} /> Launch Tour
            </button>
            <a className="btn btn-outline" href={tour.tourUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={15} /> Open in new tab
            </a>
            {canManage && (
              <button className="btn btn-outline" onClick={openEdit}>
                <Edit3 size={15} /> Edit Tour
              </button>
            )}
            {canManage && (
              <button className="btn btn-danger" onClick={() => setDeleteOpen(true)}>
                <Trash2 size={15} /> Delete Tour
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Details grid */}
      <div className="content-details__grid">
        {/* Tour details */}
        <div className="card">
          <div className="card-header">
            <h3 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>
              Tour Details
            </h3>
          </div>
          <div className="card-body">
            <div className="content-details__meta-list">
              <div className="content-details__meta-item">
                <div className="content-details__meta-icon"><Compass size={16} /></div>
                <div>
                  <span className="content-details__meta-label">Category</span>
                  <span className="content-details__meta-value">{tour.category}</span>
                </div>
              </div>
              <div className="content-details__meta-item">
                <div className="content-details__meta-icon"><MapPin size={16} /></div>
                <div>
                  <span className="content-details__meta-label">Location</span>
                  <span className="content-details__meta-value">{tour.location || '—'}</span>
                </div>
              </div>
              <div className="content-details__meta-item">
                <div className="content-details__meta-icon"><Clock size={16} /></div>
                <div>
                  <span className="content-details__meta-label">Duration</span>
                  <span className="content-details__meta-value">
                    {tour.duration != null ? `${tour.duration} minutes` : '—'}
                  </span>
                </div>
              </div>
              <div className="content-details__meta-item">
                <div className="content-details__meta-icon"><Globe size={16} /></div>
                <div>
                  <span className="content-details__meta-label">Tour URL</span>
                  <a
                    className="content-details__meta-value vt-detail__url"
                    href={tour.tourUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {tour.tourUrl}
                  </a>
                </div>
              </div>
            </div>

            {/* Tags */}
            {tour.tags?.length > 0 && (
              <div className="content-details__tags-section">
                <div className="content-details__tags-header">
                  <Tag size={14} />
                  <span>Tags</span>
                </div>
                <div className="content-details__tags">
                  {tour.tags.map((t) => (
                    <span key={t} className="content-lib__tag">#{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview / launch card */}
        <div className="card">
          <div className="card-header">
            <h3 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>
              Preview
            </h3>
          </div>
          <div className="card-body">
            <button className="vt-detail__preview" onClick={() => setViewing(true)} title="Launch tour">
              <img
                src={thumbnail}
                alt={tour.title}
                onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
              />
              <span className="vt-detail__preview-play"><Play size={26} /></span>
            </button>
            <p className="vt-detail__preview-hint">
              Click to open the interactive 360° tour in a popup viewer.
            </p>
          </div>
        </div>
      </div>

      {/* Used in Modules (Admins & Instructors only) */}
      {canSeeModules && (
        <div className="card">
          <div className="card-header">
            <h3 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>
              Used in Modules
            </h3>
            <span className="badge badge-dark">{associatedModules.length}</span>
          </div>
          <div className="card-body">
            {associatedModules.length === 0 ? (
              <div className="empty-state">
                <p>Not used in any module yet</p>
              </div>
            ) : (
              <div className="content-details__modules">
                {associatedModules.map((m) => (
                  <div
                    key={m.id}
                    className="content-details__module-item"
                    onClick={() => navigate(`/modules/${m.moduleId ? m.moduleId.replace(/-/g, '') : m.id}`, { state: { fromTour: true } })}
                  >
                    <div className="content-details__module-icon">
                      <BookOpen size={18} />
                    </div>
                    <div className="content-details__module-info">
                      <span className="content-details__module-title">{m.title}</span>
                      <div className="content-details__module-meta">
                        <span>{m.traineeIds.length} trainees</span>
                        <span>{m.completionRate}% complete</span>
                        <span className={`badge ${m.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                          {m.status}
                        </span>
                      </div>
                      <div className="progress-bar" style={{ marginTop: 6 }}>
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${m.completionRate}%` }}
                        />
                      </div>
                    </div>
                    <ArrowLeft size={16} style={{ transform: 'rotate(180deg)', color: 'var(--text-muted)' }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Popup iframe viewer */}
      <TourViewer tour={viewing ? tour : null} onClose={() => setViewing(false)} />

      {/* Edit modal */}
      <Modal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Virtual Tour"
        size="md"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setEditOpen(false)} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        }
      >
        <form className="vt-form" onSubmit={handleSave}>
          <label className="vt-field">
            <span>Title *</span>
            <input type="text" required value={form.title}
              onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
          </label>
          <label className="vt-field">
            <span>Subtitle</span>
            <input type="text" value={form.subtitle}
              onChange={(e) => setForm(f => ({ ...f, subtitle: e.target.value }))} />
          </label>
          <label className="vt-field">
            <span>Tour URL *</span>
            <input type="url" required value={form.tour_url}
              onChange={(e) => setForm(f => ({ ...f, tour_url: e.target.value }))}
              placeholder="https://..." />
          </label>

          {/* Thumbnail: upload a file OR paste a URL */}
          <div className="vt-field">
            <span>Thumbnail Image</span>
            <div className="vt-thumb-uploader">
              <div className="vt-thumb-preview">
                {thumbPreview
                  ? <img src={thumbPreview} alt="Thumbnail preview" onError={(e) => { e.target.style.display = 'none'; }} />
                  : <ImageIcon size={22} />}
              </div>
              <div className="vt-thumb-controls">
                <input
                  type="file"
                  accept="image/*"
                  ref={thumbInputRef}
                  style={{ display: 'none' }}
                  onChange={handleThumbFile}
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => thumbInputRef.current?.click()}
                  disabled={uploadingThumb}
                >
                  <Upload size={14} /> {uploadingThumb ? 'Uploading...' : 'Upload image'}
                </button>
                {form.thumbnail_url && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={() => setForm(f => ({ ...f, thumbnail_url: '' }))}
                    disabled={uploadingThumb}
                  >
                    Remove
                  </button>
                )}
                <input
                  type="url"
                  className="vt-thumb-url"
                  value={form.thumbnail_url}
                  onChange={(e) => setForm(f => ({ ...f, thumbnail_url: e.target.value }))}
                  placeholder="…or paste an image URL"
                />
              </div>
            </div>
          </div>

          <div className="vt-form-row">
            <label className="vt-field">
              <span>Category *</span>
              <select value={form.category}
                onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="vt-field">
              <span>Status</span>
              <select value={form.status}
                onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="Active">Active</option>
                <option value="Draft">Draft</option>
              </select>
            </label>
          </div>
          <div className="vt-form-row">
            <label className="vt-field">
              <span>Location</span>
              <input type="text" value={form.location}
                onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} />
            </label>
            <label className="vt-field">
              <span>Duration (min)</span>
              <input type="number" min="0" value={form.duration}
                onChange={(e) => setForm(f => ({ ...f, duration: e.target.value }))} />
            </label>
          </div>
          <label className="vt-field">
            <span>Description</span>
            <textarea rows={3} value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
          </label>
          <label className="vt-field">
            <span>Tags <span className="vt-muted">(comma separated)</span></span>
            <input type="text" value={form.tags}
              onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))}
              placeholder="safety, plant, 360" />
          </label>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete Virtual Tour"
        size="md"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setDeleteOpen(false)} disabled={deleting}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete Tour'}
            </button>
          </>
        }
      >
        <p>
          You are about to delete <strong>{tour.title}</strong>. This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default VirtualTourDetails;
